/** 使用一份 BufferGeometry 在 GPU 中批量绘制循环雪花，避免创建数百个 Mesh。 */
import { AdditiveBlending, Float32BufferAttribute } from 'three';
import { PointsProps, useFrame } from '@react-three/fiber';
import { useMemo, useRef } from 'react';
import { useTexture } from '@react-three/drei';
import { supportsWebp } from '../../utils/supports-webp';

type Props = PointsProps & {
    count: number;
    minRange?: number;
    maxRange?: number;
    minHeight?: number;
    speed?: number;
    size?: number;
};

function Particles({
    count,
    maxRange = 300,
    minRange = maxRange / 2,
    minHeight = 0,
    speed = 1,
    size = 2.5,
    ...props
}: Props) {
    const particles = useRef<any>(null);
    // WebP 探测失败时回退 PNG；纹理由 Drei 缓存并计入全局加载进度。
    const map = useTexture(
        `/snowflake-png-transparent-19.${supportsWebp ? 'webp' : 'png'}`,
    );

    // 每个粒子占连续三个浮点数 x/y/z。useMemo 保证只有参数变化才重新生成。
    const position = useMemo(() => {
        const positions = [];

        for (let i = 0; i < count; i++) {
            positions.push(
                Math.floor(Math.random() * maxRange - minRange),
                Math.floor(Math.random() * minRange - minHeight),
                Math.floor(Math.random() * maxRange - minRange),
            );
        }

        return new Float32BufferAttribute(positions, 3);
    }, [count, minRange, maxRange, minHeight]);

    // const opacity = useMemo(() => {
    //     const opacities = [];

    //     for (let i = 0; i < count; i++) {
    //         opacities.push(
    //             1,
    //             // Math.floor(Math.random() * minRange - minHeight),
    //             // Math.floor(Math.random() * maxRange - minRange),
    //         );
    //     }

    //     return new Float32BufferAttribute(opacities, 1);
    // }, [count]);

    // 速度同样作为自定义 BufferAttribute 写进 geometry，便于逐粒子读取。
    const velocity = useMemo(() => {
        const velocities = [];

        for (let i = 0; i < count; i++) {
            velocities.push(
                Math.floor(Math.random() * 6 - 3) * 0.1 * speed,
                Math.floor(Math.random() * 5 + 0.12) * 0.18 * speed,
                Math.floor(Math.random() * 6 - 3) * 0.1 * speed,
            );
        }

        return new Float32BufferAttribute(velocities, 3);
    }, [count, speed]);

    useFrame(() => {
        // 每帧直接修改 TypedArray，最后只设置一次 needsUpdate 上传到 GPU。
        if (particles.current) {
            for (let i = 0; i < count * 3; i += 3) {
                particles.current.geometry.attributes.position.array[i] -=
                    particles.current.geometry.attributes.velocity.array[i];
                particles.current.geometry.attributes.position.array[i + 1] -=
                    particles.current.geometry.attributes.velocity.array[i + 1];
                particles.current.geometry.attributes.position.array[i + 2] -=
                    particles.current.geometry.attributes.velocity.array[i + 2];
                // particles.current.geometry.attributes.opacity.array[i] -= 0.1;

                if (
                    particles.current.geometry.attributes.position.array[
                        i + 1
                    ] < -minRange
                ) {
                    // 雪花落出下边界后随机送回上方，形成无限循环且无需增删对象。
                    particles.current.geometry.attributes.position.array[i] =
                        Math.floor(Math.random() * maxRange - minRange);
                    particles.current.geometry.attributes.position.array[
                        i + 1
                    ] = Math.floor(Math.random() * minRange - minHeight);
                    particles.current.geometry.attributes.position.array[
                        i + 2
                    ] = Math.floor(Math.random() * maxRange - minRange);
                }
            }

            particles.current.geometry.attributes.position.needsUpdate = true;
        }
    });

    return (
        <points ref={particles} {...props}>
            {/* position 是 Three.js 内置属性；velocity 是本组件自定义属性。 */}
            <bufferGeometry attributes={{ position, velocity }} />
            <pointsMaterial
                size={size}
                map={map}
                // 加法混合让亮雪花叠加发光；关闭深度测试避免被场景几何遮挡。
                blending={AdditiveBlending}
                depthTest={false}
                transparent
                opacity={1}
            />
        </points>
    );
}

export default Particles;
